import type { UpdateRaptorJobInput, DispatchTimesInput, UpdateRaptorJobResourceGroupInput } from '@/libs/api-rest/generated/model';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { normalizeCallerId } from '@/common/application/utils/stringUtils';
import { formatDate } from '@/common/application/utils/timeUtils';
import { normalizeNullableNumber } from '@/common/application/utils/numberUtils';

const toHHmm = (time: Date | string | null | undefined): string | null => {
  if (!time) return null;
  if (typeof time === 'string') return time;
  return new Date(time).toTimeString().slice(0, 8);
};

/**
 * Formats a Date object to ISO string preserving the time components as selected by the user
 * Formats the date preserving local time components (what user selected) without UTC conversion
 * The API will use the timeZone field to interpret this datetime correctly
 * @param date Date object to format
 * @returns ISO string with local time components (e.g., "2025-12-12T10:25:00.000")
 */
const formatLocalDateTime = (date: Date): string => {
  // Extract local time components and format as ISO string without UTC conversion
  // This preserves "10:25 AM" as "10:25 AM" instead of converting to UTC
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

/**
 * Maps ERPJobFormValues to UpdateRaptorJobInput for PUT /api/v3/raptor-jobs/{id}
 * Converts form values to the format expected by the update endpoint
 */
export const mapERPJobToUpdateInput = (values: ERPJobFormValues): UpdateRaptorJobInput & { legacy: boolean } => {
  // Use timeZone from form if provided, otherwise fallback to browser timezone
  const timeZone = values.timeZone || (Intl?.DateTimeFormat?.().resolvedOptions().timeZone as string | undefined) || 'UTC';

  const payload: UpdateRaptorJobInput & { legacy: boolean } = {
    timeZone,
    legacy: values.legacy ?? true
  };

  // Required fields - only include if they have valid values
  if (values.jobTypeId != null && values.jobTypeId > 0) {
    payload.jobTypeId = values.jobTypeId;
  }

  if (values.customerId != null && values.customerId > 0) {
    payload.customerId = values.customerId;
  }

  if (values.jobSiteId != null && values.jobSiteId > 0) {
    payload.jobSiteId = values.jobSiteId;
  }

  // Dates - convert to YYYY-MM-DD format
  if (values.startDate) {
    payload.startDate = formatDate(values.startDate, 'yyyy-MM-dd')!;
  }

  // Format endDate if it's an overnight job (endDate is provided AND overnightJob is true)
  const isOvernight = values.overnightJob ?? false;
  payload.endDate = values.endDate && isOvernight ? formatDate(values.endDate, 'yyyy-MM-dd') : null;

  // Optional string fields
  if (values.name !== undefined) {
    payload.name = values.name || null;
  }

  if (values.jobDescription !== undefined) {
    payload.description = values.jobDescription || null;
  }

  if (values.jobNotes !== undefined) {
    payload.internalNotes = values.jobNotes || null;
  }

  if (values.notesForCrew !== undefined) {
    payload.crewNotes = values.notesForCrew || null;
  }

  // Date/time fields - convert to ISO strings preserving local time components
  // Format preserving what the user selected (e.g., "10:25 AM" stays as "10:25 AM")
  // The API will use the timeZone field to interpret this datetime correctly
  if (values.calledAt !== undefined) {
    if (values.calledAt === null) {
      payload.calledAt = null;
    } else if (values.calledAt instanceof Date) {
      // Format preserving local time components (not converting to UTC)
      payload.calledAt = formatLocalDateTime(values.calledAt);
    } else {
      payload.calledAt = values.calledAt;
    }
  }

  if (values.confirmedAt !== undefined) {
    if (values.confirmedAt === null) {
      payload.confirmedAt = null;
    } else if (values.confirmedAt instanceof Date) {
      // Format preserving local time components (not converting to UTC)
      payload.confirmedAt = formatLocalDateTime(values.confirmedAt);
    } else {
      payload.confirmedAt = values.confirmedAt;
    }
  }

  // Optional number fields
  if (values.callerId !== undefined) {
    payload.callerId = normalizeCallerId(values.callerId);
  }

  if (values.confirmedBy !== undefined) {
    payload.confirmedBy = values.confirmedBy != null ? String(values.confirmedBy) : null;
  }

  // Boolean fields
  if (values.cod !== undefined) {
    payload.cod = values.cod ?? null;
  }

  // Optional string fields
  if (values.poNumber !== undefined) {
    payload.poNumber = values.poNumber || null;
  }

  // Optional number fields
  if (values.salesAgentId !== undefined) {
    payload.salesAgentId = values.salesAgentId ?? null;
  }

  if (values.salesRegionId !== undefined) {
    payload.salesRegionId = values.salesRegionId ?? null;
  }

  if (values.vendorId !== undefined) {
    payload.vendorId = normalizeNullableNumber(values.vendorId);
  }

  if (values.flag1Id !== undefined) {
    payload.flag1Id = values.flag1Id ?? null;
  }

  if (values.flag2Id !== undefined) {
    payload.flag2Id = values.flag2Id ?? null;
  }

  if (values.flag3Id !== undefined) {
    payload.flag3Id = values.flag3Id ?? null;
  }

  // Status ID field
  if (values.statusId !== undefined) {
    payload.statusId = values.statusId ?? null;
  }

  // Sequence field - send as string
  if (values.sequence !== undefined) {
    payload.sequence = values.sequence ?? null;
  }

  // Dispatcher and dispatch location
  if (values.dispatcherId !== undefined) {
    payload.dispatcherId = values.dispatcherId ?? null;
  }

  if (values.dispatchLocationId !== undefined) {
    payload.dispatchLocationId = values.dispatchLocationId ?? null;
  }

  // Estimated fields
  if (values.estimatedVolume !== undefined) {
    payload.estimatedVolume = values.estimatedVolume ?? null;
  }

  if (values.estimatedDuration !== undefined) {
    // Map estimatedDuration to estimatedHours for PUT endpoint
    payload.estimatedHours = values.estimatedDuration ?? null;
  }

  if (values.estimatedArea !== undefined) {
    payload.estimatedArea = values.estimatedArea ?? null;
  }

  if (values.estimatedClearedTime !== undefined) {
    payload.estimatedClearedTime = toHHmm(values.estimatedClearedTime) ?? null;
  }

  // Dispatch times
  if (values.startTime || values.endTime || values.showUpTime || values.leaveByTime || values.onJobTime) {
    const dispatchTimes: DispatchTimesInput & { endTime?: string | null } = {};
    if (values.startTime) dispatchTimes.startTime = toHHmm(values.startTime);
    if (values.endTime) dispatchTimes.endTime = toHHmm(values.endTime);
    if (values.showUpTime) dispatchTimes.showUpTime = toHHmm(values.showUpTime);
    if (values.leaveByTime) dispatchTimes.leaveTime = toHHmm(values.leaveByTime);
    if (values.onJobTime) dispatchTimes.onJobTime = toHHmm(values.onJobTime);
    payload.dispatchTimes = dispatchTimes;
  }

  // Resources
  if (
    values.equipmentSizeId != null ||
    (values.employees && values.employees.length > 0) ||
    (values.extraSystems && values.extraSystems.length > 0)
  ) {
    const resources: UpdateRaptorJobResourceGroupInput[] = [
      {
        equipmentIds: values.equipmentSizeId != null ? [values.equipmentSizeId] : null,
        crew:
          values.employees && values.employees.length > 0
            ? values.employees.map((employee) => ({
                roleId: employee.roleId,
                employeeId: employee.employeeId ?? null
              }))
            : null,
        items:
          values.extraSystems && values.extraSystems.length > 0
            ? values.extraSystems.map((extraSystem) => extraSystem.extraSystemId).filter((id): id is number => id != null)
            : null
      }
    ];
    payload.resources = resources;
  }

  return payload;
};
